import { Component, OnInit, ViewChild, SimpleChanges, SimpleChange, ViewEncapsulation, OnChanges, OnDestroy } from '@angular/core';
import { FlowManagementService } from '../services/flow-management.service';
import { State, process } from '@progress/kendo-data-query';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { SafeUrl, DomSanitizer } from '@angular/platform-browser';
import { Connection } from '../obj/Connection';
import { WindowService } from '@progress/kendo-angular-dialog';
import { FlowDetailComponent } from '../flows/flow.detail.component';
//import { AnalyticsService } from '../services/analytics.service';
import { StorageRouterService } from '../services/storage-router.service';
import { Settings } from '../flows/settings';
import { FlowEvent, FlowEventService } from '../services/flow-event.service';
import { ApplicationInsightsService } from '../services/application-insights.service';
import { MsalHelperService } from '../services/msal-helper.service';

@Component({
    selector: 'app-connections',
    templateUrl: './connections.component.html',
    styleUrls: ['./connections.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class ConnectionsComponent implements OnInit, OnChanges, OnDestroy {

  apiRoot: string = "https://flow.microsoft.com";
  navigationSubscription: Subscription;
  environment: string;
  connections: any[] = [];
  state: State = {
    skip: 0,
    take: 100, // bounded — virtual scroll windows further; updates via dataStateChange

    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: [] //[{ field: 'ProductName', operator: 'contains', value: 'Chef' }]
    },
    sort: [
      {
        'field': 'displayName',
        'dir': 'asc'
      }
    ]
  };
  view: GridDataResult = process(this.connections, this.state);
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  adminLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loaded = false;
  private subscription = new Subscription();
  token_pa = null;

  @ViewChild('connectionsgrid', {static: true}) grid;

  constructor(
    private flowManagementService: FlowManagementService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private windowService: WindowService,
    //private analytics: AnalyticsService,
    private appInsights: ApplicationInsightsService,
    private storageDexieService: StorageRouterService,
    private flowEventService: FlowEventService,
    private msalHelperService: MsalHelperService
  ) { }

  ngOnInit() {

    this.token_pa = this.msalHelperService.getCachedTokenInternal(['https://service.powerapps.com/User']);
    this.storageDexieService.putConfig(Settings.SETTING_LASTROUTE, {
      'key': Settings.SETTING_LASTROUTE,
      'value': '/connections'
    });
    this.subscription.add(
      this.flowManagementService.currentEnvironment$.subscribe((environment) => {
        this.refreshEnvironment(environment);
      })
    );
    this.subscription.add(
      this.navigationSubscription = this.router.events.subscribe((e: any) => {
        // If it is a NavigationEnd event re-initalise the component
        if (e instanceof NavigationEnd) {
          this.refreshEnvironment(this.environment, true);
        }
      })
    );
    this.checkToken();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  checkToken() {
    if (this.flowManagementService && this.flowManagementService.currentEnvironment) {
      this.refreshEnvironment(this.flowManagementService.currentEnvironment);
      return;
    }
    setTimeout(this.checkToken, 500);
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.state = state;
    this.view = process(this.connections, this.state);
  }

  refreshConnectionsGrid() {
    // Don't override state.take — leave it at whatever pagination/virtual-scroll
    // size the user picked. Setting it to connections.length defeats virtualisation
    // and pulls every row through the kendo data pipeline (huge OOM risk on big tenants).
    this.view = process(this.connections, this.state);
  }

  ngOnChanges(changes: SimpleChanges) {
    const runs: SimpleChange = changes.runs;
    if (runs.currentValue != runs.previousValue) {
      this.refreshConnectionsGrid();
    }
  }

  getSafeUrl(url): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  get ready(): boolean {
    var env = this.flowManagementService.currentEnvironment;
    return this.loaded && !!env;
  }

  hasConnections(dataItem: any, index: number) {
    return dataItem.connections.length > 0;
  }

  private mergeConnectionLists(userConnections: Connection[], adminConnections: Connection[]): Connection[] {
    // O(n) dedup via Map — earlier filter+findIndex was O(n²).
    const seen = new Map<string, Connection>();
    const all = [...(userConnections || []), ...(adminConnections || [])];
    for (const c of all) {
      if (!c) continue;
      const key = `${c.name || ''}|${c.apiId || ''}`;
      if (!seen.has(key)) seen.set(key, c);
    }
    return [...seen.values()];
  }

  private applyConnections(connections: Connection[]) {
    // Split once, then build a Map<connectionId, sharedFlows[]> in a single pass
    // instead of O(n²) double-loop. Avoids OOM on large tenants where naive
    // filter-inside-forEach explodes.
    const FLOW_API = '/providers/Microsoft.PowerApps/apis/shared_logicflows';
    const shares: Connection[] = [];
    const flowsByParentId = new Map<string, Connection[]>();
    for (const c of connections) {
      if (c.apiId === FLOW_API) {
        const md = (c as any).metadata || {};
        for (const k of Object.keys(md)) {
          const parentId = md[k];
          if (!parentId) continue;
          let bucket = flowsByParentId.get(parentId);
          if (!bucket) { bucket = []; flowsByParentId.set(parentId, bucket); }
          bucket.push(c);
        }
      } else {
        shares.push(c);
      }
    }
    for (const parent of shares) {
      parent.connections = flowsByParentId.get((parent as any).id) || [];
    }
    this.connections = shares;
    this.refreshConnectionsGrid();
  }

  private refreshEnvironment(env: string, force: boolean = false) {
    if (!env) return;
    if (env == this.environment && !force) return;
    this.environment = env;

    this.loading$.next(true);
    this.adminLoading$.next(false);

    // Pass 0: read from cache so the grid populates instantly on revisit.
    // The cached set covers BOTH user + admin scope for this env (writes from
    // getConnectionsScoped are scope-tagged in storage). Live passes 1 + 2 below
    // refresh and replace once their network calls land.
    //
    // Env-change guard: if the user navigates between environments while this
    // promise is in flight, ignore the late resolve — applying the old env's
    // connections to a new view would flash wrong data.
    const cacheEnv = this.environment;
    this.storageDexieService.getConnections(cacheEnv)
      .then((cached) => {
        if (this.environment !== cacheEnv) return;
        if (cached && cached.length > 0) {
          this.applyConnections(cached);
          this.loading$.next(false);
          this.loaded = true;
        }
      })
      .catch((err) => {
        console.warn('[connections] cache read failed', err);
      });

    // Pass 1: user-scope connections — show immediately
    this.flowManagementService.getConnectionsUser(this.environment)
      .then((userConnections) => {
        this.applyConnections(userConnections);
        this.loading$.next(false);
        this.loaded = true;

        // Pass 2: admin-scope connections — merge in when ready
        this.adminLoading$.next(true);
        return this.flowManagementService.getConnectionsAdmin(this.environment)
          .then((adminConnections) => {
            const merged = this.mergeConnectionLists(userConnections, adminConnections);
            this.applyConnections(merged);
          })
          .catch((err) => {
            console.warn('[connections] admin-scope load failed', err);
          })
          .finally(() => {
            this.adminLoading$.next(false);
          });
      })
      .catch((err) => {
        console.warn('[connections] user-scope load failed', err);
        this.loading$.next(false);
        this.loaded = true;
      });

  }

  public doEditJSON(connection: Connection) {
    // Connector JSON now lives at its own URL keyed by the connector name
    // (e.g. shared_sql), independent of any specific tenant connection.
    const env = connection?.environment || this.environment;
    const connectorName = connection?.connector;
    if (!env || !connectorName) {
      console.warn('[connections] cannot navigate to connector edit — missing env or connector name', connection);
      return;
    }
    this.router.navigate(['/connector', env, connectorName, 'edit']);
  }

  public doEditConnectionJSON(connection: Connection) {
    const env = connection?.environment || this.environment;
    const name = connection?.name;
    if (!env || !name) {
      console.warn('[connections] cannot navigate to connection edit — missing env or connection name', connection);
      return;
    }
    this.router.navigate(['/connections', env, name, 'edit']);
  }

  public onSelect({ dataItem, item }): void {

    //this.analytics.send("connections", item);
    this.appInsights.trackEvent(`connections`, { "select": item });

    switch (item) {
      case "View Connector JSON": {

        this.doEditJSON(dataItem);

        break;
      }
      case "View Connection JSON": {

        this.doEditConnectionJSON(dataItem);

        break;
      }
      case "Export Excel": {
        this.grid.saveAsExcel();
        break;
      }
      default:
        break;
    }

  }

  public detailExpanded(event: { dataItem: Connection, index: number }) {
    
    this.appInsights.trackEvent(`connections`, { "select": "expand" });
    //this.analytics.send("connections", "expand");
  }

  loginPowerAppConsent() {
    this.msalHelperService.loginPowerAppConsent();
  }
}
